import React, { useEffect, useMemo, useState } from "react";
import { api, API_BASE } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import StarRating from "../components/StarRating";
import { BadgeCheck, Star, Pin, Sparkles, Trash2, Pencil, Download, Filter, Search, RefreshCw, Plus, Cloud } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from "recharts";

const SERVICES = ["Astrology Consultation", "Vedic Astrology Course", "Webinar", "Gemstone", "Rudraksha", "Other"];
const RATING_COLORS = ["#ef4444", "#f97316", "#facc15", "#10b981", "#D4AF37"];

function StatCard({ label, value, sub, testId }) {
  return (
    <div className="rounded-3xl border border-foreground/10 p-5 bg-background" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-widest text-foreground/50">{label}</div>
      <div className="mt-1 font-serif text-3xl">{value}</div>
      {sub && <div className="text-xs text-foreground/60 mt-1">{sub}</div>}
    </div>
  );
}

function EditDialog({ row, onSaved, children }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(row);
  useEffect(() => { setForm(row); }, [row, open]);

  const save = async () => {
    try {
      await api.patch(`/admin/reviews/${row.id}`, {
        name: form.name, city: form.city, service: form.service, rating: Number(form.rating),
        review: form.review, verified_google: !!form.verified_google, google_review_url: form.google_review_url || null,
      });
      toast.success("Review updated");
      onSaved?.();
      setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Edit review</DialogTitle></DialogHeader>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Name</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>City</Label><Input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
          <div><Label>Service</Label>
            <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Rating</Label>
            <div className="mt-2"><StarRating value={Number(form.rating || 0)} onChange={(n) => setForm({ ...form, rating: n })} /></div>
          </div>
          <div className="md:col-span-2"><Label>Review</Label><Textarea rows={6} value={form.review || ""} onChange={(e) => setForm({ ...form, review: e.target.value })} /></div>
          <div className="md:col-span-2 flex items-center gap-3">
            <Checkbox checked={!!form.verified_google} onCheckedChange={(v) => setForm({ ...form, verified_google: !!v })} id="verified-google" />
            <Label htmlFor="verified-google" className="cursor-pointer">Mark as Verified Google Review</Label>
          </div>
          {form.verified_google && (
            <div className="md:col-span-2"><Label>Google review URL</Label><Input value={form.google_review_url || ""} onChange={(e) => setForm({ ...form, google_review_url: e.target.value })} placeholder="https://www.google.com/maps/..." /></div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} data-testid="edit-save" className="bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A]">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddGoogleDialog({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", city: "", service: "Astrology Consultation", rating: 5, review: "", google_review_url: "" });
  const save = async () => {
    if (!form.name || !form.review) return toast.error("Name and review are required.");
    try {
      await api.post(`/admin/reviews`, {
        ...form, rating: Number(form.rating), status: "approved", source: "google",
        verified_google: true, featured: false, pinned: false,
      });
      toast.success("Google review added");
      setOpen(false);
      setForm({ name: "", city: "", service: "Astrology Consultation", rating: 5, review: "", google_review_url: "" });
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="btn-add-google" className="rounded-full border-emerald-500/40 text-emerald-600 hover:bg-emerald-500/10">
          <BadgeCheck className="w-4 h-4 mr-2" />Add Google Review
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Add verified Google review</DialogTitle></DialogHeader>
        <p className="text-xs text-foreground/60 -mt-2">Paste in a review from your Google Business Profile. It will show with a &ldquo;Verified Google&rdquo; badge.</p>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Reviewer name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
          <div><Label>Service</Label>
            <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Rating</Label>
            <div className="mt-2"><StarRating value={Number(form.rating)} onChange={(n) => setForm({ ...form, rating: n })} /></div>
          </div>
          <div className="md:col-span-2"><Label>Review text</Label><Textarea rows={6} value={form.review} onChange={(e) => setForm({ ...form, review: e.target.value })} /></div>
          <div className="md:col-span-2"><Label>Google review link (optional)</Label><Input placeholder="https://www.google.com/maps/..." value={form.google_review_url} onChange={(e) => setForm({ ...form, google_review_url: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} data-testid="add-google-save" className="bg-emerald-600 hover:bg-emerald-700 text-white">Add review</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminReviews() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ distribution: {}, by_service: {}, trend: [] });
  const [dashStats, setDashStats] = useState({ pending_reviews: 0 });
  const [gStatus, setGStatus] = useState({ configured: false, connected: false, note: "" });
  const [status, setStatus] = useState("all");
  const [service, setService] = useState("all");
  const [rating, setRating] = useState("all");
  const [source, setSource] = useState("all");
  const [q, setQ] = useState("");

  const load = () => {
    const params = new URLSearchParams();
    if (status !== "all") params.set("status", status);
    if (service !== "all") params.set("service", service);
    if (rating !== "all") params.set("rating", String(rating));
    if (source !== "all") params.set("source", source);
    if (q) params.set("q", q);
    api.get(`/admin/reviews?${params}`).then((r) => setRows(r.data));
    api.get("/reviews/stats").then((r) => setStats(r.data));
    api.get("/admin/stats").then((r) => setDashStats(r.data)).catch(() => {});
    api.get("/admin/google/status").then((r) => setGStatus(r.data)).catch(() => {});
  };
  useEffect(load, [status, service, rating, source, q]);

  const setField = async (id, patch) => {
    try { await api.patch(`/admin/reviews/${id}`, patch); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    await api.delete(`/admin/reviews/${id}`);
    toast.success("Deleted"); load();
  };

  const download = async () => {
    const token = localStorage.getItem("yah_token");
    const r = await fetch(`${API_BASE}/admin/reviews/export.csv`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `yourastrohub-reviews-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const googleConnect = async () => {
    try {
      const { data } = await api.get("/admin/google/login");
      window.location.href = data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Google OAuth not configured. See setup notes."); }
  };
  const googleSync = async () => {
    try {
      const { data } = await api.post("/admin/google/sync");
      toast.success(`Synced ${data.synced} reviews from Google`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
  };

  const ratingBars = useMemo(() => (
    [5, 4, 3, 2, 1].map((n) => ({ rating: `${n}★`, count: Number(stats.distribution?.[String(n)] || 0) }))
  ), [stats]);
  const serviceSlices = useMemo(() => (
    Object.entries(stats.by_service || {}).map(([name, value], i) => ({ name, value, fill: RATING_COLORS[i % RATING_COLORS.length] }))
  ), [stats]);

  return (
    <div data-testid="admin-reviews" className="space-y-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-4xl">Reviews & Testimonials</h1>
          <p className="text-foreground/60 mt-1">Moderate, edit and publish client feedback.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <AddGoogleDialog onSaved={load} />
          <Button variant="outline" onClick={download} data-testid="btn-export-csv" className="rounded-full">
            <Download className="w-4 h-4 mr-2" />Export CSV
          </Button>
          <Button variant="outline" onClick={load} className="rounded-full"><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total reviews" value={stats.total ?? 0} testId="stat-total" />
        <StatCard label="Average rating" value={stats.average_rating ?? 0} sub="out of 5" testId="stat-avg" />
        <StatCard label="This month" value={stats.this_month ?? 0} testId="stat-month" />
        <StatCard label="Pending moderation" value={dashStats.pending_reviews ?? 0} testId="stat-pending" />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="rounded-3xl border border-foreground/10 p-5 bg-background">
          <div className="eyebrow mb-2">Rating distribution</div>
          <div className="h-52">
            <ResponsiveContainer><BarChart data={ratingBars}>
              <XAxis dataKey="rating" stroke="#888" fontSize={12} />
              <YAxis stroke="#888" fontSize={12} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill="#D4AF37" radius={[6, 6, 0, 0]} />
            </BarChart></ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-3xl border border-foreground/10 p-5 bg-background">
          <div className="eyebrow mb-2">By service</div>
          <div className="h-52">
            <ResponsiveContainer><PieChart>
              <Pie data={serviceSlices} dataKey="value" nameKey="name" innerRadius={40} outerRadius={80}>
                {serviceSlices.map((s, i) => <Cell key={i} fill={s.fill} />)}
              </Pie>
              <Tooltip />
            </PieChart></ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-3xl border border-foreground/10 p-5 bg-background">
          <div className="eyebrow mb-2">Monthly trend (12 mo)</div>
          <div className="h-52">
            <ResponsiveContainer><LineChart data={stats.trend || []}>
              <XAxis dataKey="month" stroke="#888" fontSize={10} tickFormatter={(m) => m.slice(5)} />
              <YAxis stroke="#888" fontSize={12} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart></ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Google Business Profile */}
      <div className="rounded-3xl border border-emerald-500/30 p-5 bg-emerald-50/60 dark:bg-emerald-950/20">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-serif text-lg flex items-center gap-2"><Cloud className="w-4 h-4 text-emerald-600" /> Google Business Profile Sync</div>
            <p className="text-xs text-foreground/70 mt-1 max-w-2xl">{gStatus.note}</p>
            <div className="text-[11px] uppercase tracking-widest mt-2 text-foreground/60">
              Configured: <span className={gStatus.configured ? "text-emerald-600" : "text-rose-500"}>{gStatus.configured ? "yes" : "no"}</span>
              {" · "}Connected: <span className={gStatus.connected ? "text-emerald-600" : "text-rose-500"}>{gStatus.connected ? "yes" : "no"}</span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={googleConnect} disabled={!gStatus.configured} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">Connect Google</Button>
            <Button size="sm" onClick={googleSync} disabled={!gStatus.connected} variant="outline" className="rounded-full">Sync now</Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Filter className="w-4 h-4 text-foreground/60" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="rounded-full w-40" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Select value={service} onValueChange={setService}>
          <SelectTrigger className="rounded-full w-52"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All services</SelectItem>
            {SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={rating} onValueChange={setRating}>
          <SelectTrigger className="rounded-full w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All ratings</SelectItem>
            {[5, 4, 3, 2, 1].map((n) => <SelectItem key={n} value={String(n)}>{n} star{n > 1 ? "s" : ""}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="rounded-full w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            <SelectItem value="website">Website</SelectItem>
            <SelectItem value="google">Google</SelectItem>
            <SelectItem value="manual">Manual</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="rounded-full pl-8 w-56" data-testid="filter-q" />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-3xl border border-foreground/10 overflow-hidden bg-background">
        <table className="w-full text-sm">
          <thead className="text-left bg-[#F4EFE6]/60 dark:bg-[#0F284B]/60">
            <tr>
              <th className="p-3">Reviewer</th>
              <th className="p-3">Service</th>
              <th className="p-3">Rating</th>
              <th className="p-3 max-w-md">Review</th>
              <th className="p-3">Source</th>
              <th className="p-3">Status</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7} className="p-8 text-center text-foreground/50">No reviews match your filters.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} data-testid={`review-row-${r.id}`} className="border-t border-foreground/10 align-top">
                <td className="p-3">
                  <div className="font-medium flex items-center gap-1">
                    {r.pinned && <Pin className="w-3 h-3 text-[#D4AF37]" />}
                    {r.featured && <Sparkles className="w-3 h-3 text-[#D4AF37]" />}
                    {r.name}
                  </div>
                  <div className="text-xs text-foreground/60">{r.city || "—"}</div>
                  <div className="text-[11px] text-foreground/50 mt-1">{(r.created_at || "").slice(0, 10)}</div>
                </td>
                <td className="p-3 text-xs">{r.service}</td>
                <td className="p-3"><StarRating value={r.rating} readOnly size="sm" /></td>
                <td className="p-3 max-w-md">
                  <p className="text-sm line-clamp-3">{r.review}</p>
                  {r.verified_google && (
                    <span className="inline-flex items-center gap-1 mt-1 text-[10px] uppercase tracking-widest text-emerald-600">
                      <BadgeCheck className="w-3 h-3" />Google verified
                    </span>
                  )}
                </td>
                <td className="p-3 text-xs capitalize">{r.source}</td>
                <td className="p-3">
                  <Select value={r.status} onValueChange={(v) => setField(r.id, { status: v })}>
                    <SelectTrigger className="rounded-full w-32 text-xs h-8" data-testid={`status-${r.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approve</SelectItem>
                      <SelectItem value="rejected">Reject</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="p-3">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant={r.pinned ? "default" : "outline"} onClick={() => setField(r.id, { pinned: !r.pinned })}
                      data-testid={`pin-${r.id}`} className={`rounded-full h-8 w-8 p-0 ${r.pinned ? "bg-[#D4AF37] text-[#0B1F3A] hover:bg-[#AD974F]" : ""}`}>
                      <Pin className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant={r.featured ? "default" : "outline"} onClick={() => setField(r.id, { featured: !r.featured })}
                      data-testid={`feature-${r.id}`} className={`rounded-full h-8 w-8 p-0 ${r.featured ? "bg-[#D4AF37] text-[#0B1F3A] hover:bg-[#AD974F]" : ""}`}>
                      <Sparkles className="w-3.5 h-3.5" />
                    </Button>
                    <EditDialog row={r} onSaved={load}>
                      <Button size="sm" variant="outline" data-testid={`edit-${r.id}`} className="rounded-full h-8 w-8 p-0"><Pencil className="w-3.5 h-3.5" /></Button>
                    </EditDialog>
                    <Button size="sm" variant="outline" onClick={() => del(r.id)} data-testid={`delete-${r.id}`} className="rounded-full h-8 w-8 p-0 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/50"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
